import React, { useEffect, useMemo } from 'react';
import PlanDetailsItem from '../PlanDetails/PlanDetailsItem/PlanDetailsItem';
import { formatImproveStatusLabel } from '../../../../Helpers/Tasks';
import { ImproveTaskDetail } from '../../../../Types/Improve/ImprovementPlanDetail';
import Button from '../../../Utils/Button/Button';
import CommentsIcon from '../../../Icons/CommentsIcon/CommentsIcon';
import DatePicker from '../../../Utils/DatePicker/DatePicker';
import DropDownList from '../../../Utils/DropDownList/DropDownList';
import { axiosInstance } from '../../../../Authentication/AxiosInterceptor';
import { IDropDownListItem } from '../../../../Types/DropDownList/IDropDownListItem';
import { UserDisplay } from '../../../../Types/User';

import EditableSidePane, { EditablePaneCtx } from '../../../Utils/EditableSidePane/EditableSidePane';
import { CheckNewImproveTaskVersion } from '../../../../API/Improve/Improve';
import { ImproveTemplateDto } from '../../../../Types/Improve/ImproveTemplateDto';
import { UNSET_DATE } from '../../../../Helpers/Dates';

const NOT_SET = "Not Set" as const;

const impactOptions: IDropDownListItem[] = [
  { value: 'Not_Met', text: 'Not Met', colour: undefined, textColour: undefined },
  { value: 'Partial', text: 'Partially Met', colour: undefined, textColour: undefined },
  { value: 'Met', text: 'Met', colour: undefined, textColour: undefined },
  { value: 'Exceeded', text: 'Exceeded', colour: undefined, textColour: undefined }
];

type TaskDetailsPaneProps = {
  task: ImproveTaskDetail;
  availableUsers: UserDisplay[];
  onTaskUpdated: (task: ImproveTaskDetail) => void;
  saveTaskChanges: (task: ImproveTaskDetail) => Promise<void>;
  editMode: boolean;
  template?: ImproveTemplateDto;
};

const getParsedDate = (strDate: Date | string | undefined) => {
  if (!strDate) return 'N/A';
  const dateFormatter = new Intl.DateTimeFormat('en-GB', { dateStyle: 'full' });
  return dateFormatter.format(new Date(strDate));
};

const taskTextAreaStyle = {
  width: '100%',
  boxSizing: 'border-box' as const,
  border: '1px solid var(--bs-gray-300)',
  fontSize: '0.8rem'
};

const TaskDetailsPane = ({ task, availableUsers, onTaskUpdated, editMode, saveTaskChanges, template }: TaskDetailsPaneProps) => {

  const checkNewVersion = async (data: ImproveTaskDetail) => {
    const latestTask = await CheckNewImproveTaskVersion(task.uniqueId, task.version);
    if (latestTask) onTaskUpdated(latestTask);
  };

  useEffect(() => {
    checkNewVersion(task);
  }, [task.uniqueId]);

  const userDropdownOptions: IDropDownListItem[] = useMemo(() => {
    const mapped = availableUsers.map((u) => ({
      value: u.sub,
      text: u.fullName,
      colour: undefined,
      textColour: undefined
    }));

    return [{ value: 'Not Set', text: 'Not Set', colour: undefined, textColour: undefined }, ...mapped];
  }, [availableUsers]);

  // API sends null when not enabled
  const showAssignedTo = task.assignedTo != null;
  const showTargetDate = task.targetDate != null;
  const showCompletionDate = task.completionDate != null;
  const showCustomTextD = task.customTextD != null;
  const showCustomTextE = task.customTextE != null;
  const showCustomTextF = task.customTextF != null;
  const showStatus = task.taskStatus != null;

  const showDetails =
    showAssignedTo ||
    showTargetDate ||
    showCompletionDate ||
    showCustomTextD ||
    showCustomTextE ||
    showCustomTextF;

  const renderText = (value?: string | null) => (
    value?.trim() ? value : 'Not Set'
  );

  return (
    <EditableSidePane<ImproveTaskDetail>
      locked={task.lockedBy != null}
      lockedBy={task.lockedBy?.fullName}
      editMode={editMode}
      entity={task}
      entityId={task.uniqueId}
      onSave={saveTaskChanges}
      onUpdated={onTaskUpdated}
      onContentClick={() => {}}
      showDetails={showDetails}
      editText="Edit Task"
      doneText="Finished Editing"
      titleKey="taskHeading"
      titleFallback="Untitled Task"
      renderDetails={({ isEditing, data, updateDraft }: EditablePaneCtx<ImproveTaskDetail>) => (
        <>
          {showAssignedTo && (
            <PlanDetailsItem text={`${template?.taskAssignedTo?.name ?? "Assigned To"}:`} row={!isEditing}>
              {isEditing ? (
                <DropDownList
                  placeholder="Select a user"
                  data={userDropdownOptions}
                  value={data.assignedTo?.sub ?? ''}
                  onChange={(item) => {
                    let picked = item.value === NOT_SET ? {sub: NOT_SET, fullName: NOT_SET} : availableUsers.find((u) => u.sub === item.value);
                    updateDraft('assignedTo', (picked ?? null) as any);
                  }}
                />
              ) : (
                renderText(data.assignedTo?.fullName)
              )}
            </PlanDetailsItem>
          )}

          {showTargetDate && (
            <PlanDetailsItem text={`${template?.taskTargetDate?.name ?? "Target Date"}:`} row>
              {isEditing ? (
                <DatePicker value={data.targetDate} onChange={(v) => updateDraft('targetDate', v as any)} />
              ) : (
                <div>
                  {getParsedDate(data.targetDate)}
                </div>
              )}
            </PlanDetailsItem>
          )}

          {showCompletionDate && (
            <PlanDetailsItem text={`${template?.taskCompletionDate?.name ?? "Completion Date"}:`} row>
              {isEditing ? (
                <DatePicker value={data.completionDate} onChange={(v) => updateDraft('completionDate', v as any)} />
              ) : (
                <div>
                  {getParsedDate(data.completionDate)}
                </div>
              )}
            </PlanDetailsItem>
          )}

          {showStatus && (
          <PlanDetailsItem text={`${template?.status?.name ?? "Status"}:`} row={!isEditing}>
            {isEditing ? (
              <DropDownList
                value={(data.taskStatus ?? '') as any}
                placeholder="Select status..."
                data={impactOptions}
                clearable
                onChange={(item) => {
                  updateDraft('taskStatus', item.value as any);
                }}
              />
            ) : (
              renderText(data.taskStatus ? formatImproveStatusLabel(data.taskStatus) : null)
            )}
          </PlanDetailsItem>
          )}

          {showCustomTextD && (
            <PlanDetailsItem text={`${template?.columnD?.name ?? "Column D"}:`} column={isEditing}>
              {isEditing ? (
                <textarea
                  style={taskTextAreaStyle}
                  value={(data.customTextD ?? '') as any}
                  onChange={(e) => updateDraft('customTextD', e.target.value as any)}
                />
              ) : (
                renderText(data.customTextD as any)
              )}
            </PlanDetailsItem>
          )}

          {showCustomTextE && (
            <PlanDetailsItem text={`${template?.columnE?.name ?? "Column E"}:`} column={isEditing}>
              {isEditing ? (
                <textarea
                  style={taskTextAreaStyle}
                  value={(data.customTextE ?? '') as any}
                  onChange={(e) => updateDraft('customTextE', e.target.value as any)}
                />
              ) : (
                renderText(data.customTextE as any)
              )}
            </PlanDetailsItem>
          )}

          {showCustomTextF && (
            <PlanDetailsItem text={`${template?.columnF?.name ?? "Column F"}:`} column={isEditing}>
              {isEditing ? (
                <textarea
                  style={taskTextAreaStyle}
                  value={(data.customTextF ?? '') as any}
                  onChange={(e) => updateDraft('customTextF', e.target.value as any)}
                />
              ) : (
                renderText(data.customTextF as any)
              )}
            </PlanDetailsItem>
          )}

          {showCustomTextF && (
            <PlanDetailsItem text={`${template?.customTagB?.name ?? "Tag Column B"}:`} column={isEditing}>
              {isEditing ? (
                <input
                  style={{ border: '1px solid var(--bs-gray-300)', fontSize: '0.8rem' }}
                  value={(data.customTagsB ?? '') as any}
                  onChange={(e) => updateDraft('customTagsB', e.target.value as any)}
                />
              ) : (
                renderText(data.customTagsB as any)
              )}
            </PlanDetailsItem>
          )}
        </>
      )}
    />
  );
};

export default TaskDetailsPane;
